import { useState } from "react";
import ChevronDown from "../../../../svg/chevron-down";
import { FaxFormData, FormField, FormFieldConfig } from "../../types";
import PreviousDrugsTable from "../previousDrugsTable/previousDrugsTable";
import PreviousTreatmentsTable from "../previousTreatmentsTable/previousTreatmentsTable";

interface FormSectionProps {
  section: FormFieldConfig;
  formData: FaxFormData;
  validationErrors: Record<string, string>;
  onFieldChange: (fieldName: string, value: any) => void;
  isLast: boolean;
}

const FormSection = ({
  section,
  formData,
  validationErrors,
  onFieldChange,
  isLast,
}: FormSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(section.expanded !== false);

  const shouldShowField = (field: FormField): boolean => {
    if (!field.dependsOn || !field.dependsValue) return true;

    const dependentValue = formData[field.dependsOn as keyof FaxFormData];
    const expectedValues = field.dependsValue.split(",");

    // Debug logging
    if (field.dependsOn) {
      console.log(`🔍 Field: ${field.name}`);
      console.log(`   Depends on: ${field.dependsOn}`);
      console.log(`   Expected: ${field.dependsValue}`);
      console.log(`   Current value: ${dependentValue}`);
      console.log(
        `   Should show: ${expectedValues.includes(String(dependentValue))}`,
      );
    }

    return expectedValues.includes(String(dependentValue));
  };

  const renderField = (field: FormField) => {
    const fieldValue = formData[field.name as keyof FaxFormData];
    const hasError = validationErrors[field.name];
    const isRequired = field.required;

    if (!shouldShowField(field)) {
      return null;
    }

    const baseClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-tertiaryBlue-4 focus:border-tertiaryBlue-4 ${
      hasError ? "border-red-500 bg-red-50" : "border-primaryGray-12 bg-white"
    }`;

    switch (field.type) {
      case "text":
      case "number":
        return (
          <div key={field.name} className="form-field">
            <label className="mb-1 block text-sm font-medium text-primaryGray-1">
              {field.label}
              {isRequired && <span className="ml-1 text-red-500">*</span>}
            </label>
            <input
              type={field.type}
              name={field.name}
              value={
                typeof fieldValue === "boolean"
                  ? String(fieldValue)
                  : typeof fieldValue === "string" ||
                      typeof fieldValue === "number"
                    ? fieldValue
                    : ""
              }
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              className={baseClasses}
            />
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      case "date":
        return (
          <div key={field.name} className="form-field">
            <label className="mb-1 block text-sm font-medium text-primaryGray-1">
              {field.label}
              {isRequired && <span className="ml-1 text-red-500">*</span>}
            </label>
            <input
              type="date"
              name={field.name}
              value={
                typeof fieldValue === "boolean"
                  ? String(fieldValue)
                  : typeof fieldValue === "string" ||
                      typeof fieldValue === "number"
                    ? fieldValue
                    : ""
              }
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              className={baseClasses}
            />
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={field.name} className="form-field">
            <label className="mb-1 block text-sm font-medium text-primaryGray-1">
              {field.label}
              {isRequired && <span className="ml-1 text-red-500">*</span>}
            </label>
            <textarea
              name={field.name}
              value={
                typeof fieldValue === "boolean"
                  ? String(fieldValue)
                  : typeof fieldValue === "string" ||
                      typeof fieldValue === "number"
                    ? fieldValue
                    : ""
              }
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              className={baseClasses}
            />
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      case "select":
        return (
          <div key={field.name} className="form-field">
            <label className="mb-1 block text-sm font-medium text-primaryGray-1">
              {field.label}
              {isRequired && <span className="ml-1 text-red-500">*</span>}
            </label>
            <select
              name={field.name}
              value={
                typeof fieldValue === "boolean"
                  ? String(fieldValue)
                  : typeof fieldValue === "string" ||
                      typeof fieldValue === "number"
                    ? fieldValue
                    : ""
              }
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              className={baseClasses}
            >
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      case "radio":
        return (
          <div key={field.name} className="form-field">
            <label className="mb-2 block text-sm font-medium text-primaryGray-1">
              {field.label}
              {isRequired && <span className="ml-1 text-red-500">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option) => (
                <div key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name={field.name}
                    id={`${field.name}_${option.value}`}
                    value={option.value}
                    checked={String(fieldValue) === option.value}
                    onChange={(e) =>
                      onFieldChange(
                        field.name,
                        e.target.value === "true"
                          ? true
                          : e.target.value === "false"
                            ? false
                            : e.target.value,
                      )
                    }
                    className="h-4 w-4 border-primaryGray-12 text-tertiaryBlue-4 focus:ring-tertiaryBlue-4"
                  />
                  <label
                    htmlFor={`${field.name}_${option.value}`}
                    className="ml-2 cursor-pointer text-sm text-primaryGray-1"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.name} className="form-field">
            <div className="flex items-center">
              <input
                type="checkbox"
                name={field.name}
                id={field.name}
                checked={!!fieldValue}
                onChange={(e) => onFieldChange(field.name, e.target.checked)}
                className="h-4 w-4 rounded border-primaryGray-12 text-tertiaryBlue-4 focus:ring-tertiaryBlue-4"
              />
              <label
                htmlFor={field.name}
                className="ml-2 cursor-pointer text-sm text-primaryGray-1"
              >
                {field.label}
                {isRequired && <span className="ml-1 text-red-500">*</span>}
              </label>
            </div>
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      case "previousTreatments":
        return (
          <div key={field.name} className="form-field col-span-full">
            <PreviousTreatmentsTable
              value={fieldValue as any}
              onChange={(value) => onFieldChange(field.name, value)}
            />
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      case "previousDrugs":
        return (
          <div key={field.name} className="form-field col-span-full">
            <PreviousDrugsTable
              value={fieldValue as any}
              onChange={(value) => onFieldChange(field.name, value)}
            />
            {hasError && (
              <p className="mt-1 text-xs text-red-500">{hasError}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`form-section overflow-hidden rounded-lg border border-primaryGray-15 ${!isLast ? "mb-4" : ""}`}
    >
      <div
        className={`form-section__header flex items-center justify-between bg-primaryGray-17 px-4 py-3 ${section.collapsible ? "cursor-pointer hover:bg-primaryGray-16" : ""}`}
        onClick={() => section.collapsible && setIsExpanded(!isExpanded)}
      >
        <h4 className="text-base font-semibold text-primaryGray-1">
          {section.title}
        </h4>
        {section.collapsible && (
          <div className="text-primaryGray-8">
            <ChevronDown className="h-5 w-5" rotate={isExpanded ? 180 : 0} />
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="form-section__content p-4">
          <div className="space-y-4">
            {section.fields.map((field) => {
              const fieldElement = renderField(field);
              if (fieldElement && field.dependsOn) {
                // Add special styling for conditional fields
                return (
                  <div
                    key={field.name}
                    className="ml-4 border-l-2 border-blue-200 pl-4"
                  >
                    {fieldElement}
                  </div>
                );
              }
              return fieldElement;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSection;
